// src/edu-world/systems/progression-system.ts
// Progression System — tracks advancement through world changes (no visible scores)
// Standalone module (Phase 3) — pure logic, Phaser-agnostic
// Requirements: 10.1, 10.2, 10.3, 10.4, 10.6, 10.7

// ─── Types ───

export type ProgressionArea = 'community' | 'environment';

export interface ProgressionAreaState {
  area: ProgressionArea;
  completedMissions: string[];
  thresholdTarget: number;
  thresholdReached: boolean;
  thresholdReachedAt?: number;
}

export interface WorldEvolutionEvent {
  id: string;
  type: 'new_visual' | 'new_npc' | 'zone_expansion';
  targetZone: string;
  assetRef: string;
  celebrationAnimation: string;
  triggeredAt: number;
}

export interface ProgressionConfig {
  communityThreshold: number;
  environmentThreshold: number;
  evolutionEvents: EvolutionEventConfig[];
}

export interface EvolutionEventConfig {
  area: ProgressionArea;
  type: 'new_visual' | 'new_npc' | 'zone_expansion';
  targetZone: string;
  assetRef: string;
  celebrationAnimation: string;
}

// ─── Default Configuration ───

const DEFAULT_CONFIG: ProgressionConfig = {
  communityThreshold: 3,
  environmentThreshold: 3,
  evolutionEvents: [
    {
      area: 'community',
      type: 'new_npc',
      targetZone: 'parque',
      assetRef: 'npc-new-friend',
      celebrationAnimation: 'confetti-burst',
    },
    {
      area: 'environment',
      type: 'new_visual',
      targetZone: 'parque',
      assetRef: 'garden-expanded',
      celebrationAnimation: 'rainbow-arc',
    },
  ],
};

// ─── ProgressionSystem ───

export class ProgressionSystem {
  private areas: Map<ProgressionArea, ProgressionAreaState>;
  private triggeredEvents: WorldEvolutionEvent[];
  private config: ProgressionConfig;

  constructor(config?: Partial<ProgressionConfig>) {
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.triggeredEvents = [];

    this.areas = new Map<ProgressionArea, ProgressionAreaState>([
      [
        'community',
        {
          area: 'community',
          completedMissions: [],
          thresholdTarget: this.config.communityThreshold,
          thresholdReached: false,
        },
      ],
      [
        'environment',
        {
          area: 'environment',
          completedMissions: [],
          thresholdTarget: this.config.environmentThreshold,
          thresholdReached: false,
        },
      ],
    ]);
  }

  /**
   * Record a completed mission in the specified area.
   * Returns any triggered evolution events (empty array if threshold not reached).
   * Visible exclusively through world changes — no numeric scores or bars.
   */
  recordMissionComplete(area: ProgressionArea, missionId: string): WorldEvolutionEvent[] {
    const state = this.areas.get(area);
    if (!state) return [];

    // Prevent duplicate recording
    if (state.completedMissions.includes(missionId)) {
      return [];
    }

    state.completedMissions.push(missionId);

    // Check if threshold is newly reached
    if (!state.thresholdReached && state.completedMissions.length >= state.thresholdTarget) {
      state.thresholdReached = true;
      state.thresholdReachedAt = Date.now();

      // Trigger world evolution events for this area
      return this.triggerEvolutionEvents(area);
    }

    return [];
  }

  /**
   * Get the progression state for an area.
   * Note: This returns internal state for system use — the child never sees numeric data.
   */
  getProgress(area: ProgressionArea): ProgressionAreaState | null {
    const state = this.areas.get(area);
    if (!state) return null;
    return { ...state, completedMissions: [...state.completedMissions] };
  }

  /**
   * Check whether the threshold has been reached for an area.
   */
  hasReachedThreshold(area: ProgressionArea): boolean {
    const state = this.areas.get(area);
    return state?.thresholdReached ?? false;
  }

  /**
   * Get all evolution triggers that have been activated.
   * These represent world changes visible to the child.
   */
  getEvolutionTriggers(): WorldEvolutionEvent[] {
    return [...this.triggeredEvents];
  }

  /**
   * Get the number of completed missions in a given area.
   */
  getMissionCount(area: ProgressionArea): number {
    const state = this.areas.get(area);
    return state?.completedMissions.length ?? 0;
  }

  /**
   * Get the total number of completed missions across all areas.
   */
  getTotalMissionCount(): number {
    let total = 0;
    for (const state of this.areas.values()) {
      total += state.completedMissions.length;
    }
    return total;
  }

  /**
   * Reset progression (for testing or new game).
   */
  reset(): void {
    for (const state of this.areas.values()) {
      state.completedMissions = [];
      state.thresholdReached = false;
      state.thresholdReachedAt = undefined;
    }
    this.triggeredEvents = [];
  }

  // ─── Private ───

  private triggerEvolutionEvents(area: ProgressionArea): WorldEvolutionEvent[] {
    const events: WorldEvolutionEvent[] = [];

    const eventConfigs = this.config.evolutionEvents.filter((e) => e.area === area);

    for (const eventConfig of eventConfigs) {
      const event: WorldEvolutionEvent = {
        id: `evolution-${area}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        type: eventConfig.type,
        targetZone: eventConfig.targetZone,
        assetRef: eventConfig.assetRef,
        celebrationAnimation: eventConfig.celebrationAnimation,
        triggeredAt: Date.now(),
      };
      this.triggeredEvents.push(event);
      events.push(event);
    }

    return events;
  }
}
